import { PopupType } from '../../../components/popup/types';
import { CrossOriginCommunication } from '../../../controllers/crossOriginCommunication/CrossOriginCommunicationController';
import { BetStateBitMask, BetStateBitMaskTypes } from '../../../logger/bitMasks/BetStateBitMask';
import { Logger } from '../../../logger/Logger';
import { RoundIntegrityWatcher, RoundIntegrityWatcherPhases } from '../../../logger/RoundIntegrityWatcher';
import { autoplayActions } from '../../../store/slices/autoplay/slice';
import { betActions } from '../../../store/slices/bets/slice';
import { gameStateActions } from '../../../store/slices/game/slice';
import { GameStatus } from '../../../store/slices/game/types';
import { popupActions } from '../../../store/slices/popup/slice';
import { areBetsEqual } from '../../../utils/bets';
import { roundNumber } from '../../../utils/number';
import { OutgoingMessage } from '../../crossOriginCommunication/types';
import { getAcceptedBets } from '../betUtilities/getAcceptedBets';
import { getComputedBets } from '../betUtilities/getComputedBets';
import type { ComputedBet } from '../betUtilities/types';
import { type GameCommunicationMessageType, MessageName } from '../types';

import { BaseCoreGameCommunicationProcessor } from './BaseCoreGameCommunicationProcessor';

export class CoreBetsClosedGameCommunicationProcessor extends BaseCoreGameCommunicationProcessor {
  public messageName = MessageName.BetsClosed;
  public delay = 0;

  public process(_message: GameCommunicationMessageType): void {
    RoundIntegrityWatcher.setPhase(RoundIntegrityWatcherPhases.BetsClosed);
    CrossOriginCommunication.dispatch(OutgoingMessage.BetsClosed);

    this.logData();

    const tableId = this.store.getState().config.session?.tableId ?? '';

    this.store.dispatch(gameStateActions.setWinners([]));
    this.store.dispatch(gameStateActions.setCurrentGameStatus(GameStatus.BetsClosed));
    this.store.dispatch(betActions.saveLastBets({ tableId }));
    this.placeBets();
  }

  private logData(): void {
    const { bets, popup, settings } = this.store.getState();

    if (popup.currentPopup !== null) {
      if (popup.currentPopup.popupName === PopupType.Inactivity) {
        BetStateBitMask.apply(BetStateBitMaskTypes.Inactivity);
      } else if (popup.currentPopup.popupName !== PopupType.InsufficientFunds) {
        BetStateBitMask.apply(BetStateBitMaskTypes.OtherBlockingPopup);
      }
    }

    if (settings.volume.master.value > 0) {
      if (settings.volume.channels['studio'] && settings.volume.channels['studio'].value > 0) {
        BetStateBitMask.apply(BetStateBitMaskTypes.VideoSoundOn);
      }
      if (settings.volume.channels['game'] && settings.volume.channels['game'].value > 0) {
        BetStateBitMask.apply(BetStateBitMaskTypes.GameSoundOn);
      }
    }
    if (settings.stream.isEnabled) {
      BetStateBitMask.apply(BetStateBitMaskTypes.VideoOn);
    }
    Logger.logEvent('roundClosed', {
      additional: '' + (bets.betStack.length > 0),
      bandwidth: BetStateBitMask.maskedBinary,
    });

    BetStateBitMask.reset();
  }

  private computeBets(): ComputedBet[] {
    const { autoplay, bets } = this.store.getState();
    const { totalBet } = bets;
    /**
     *  We are also passing the bets from the autoplay if it is paused to handle the case the user is present for bets open
     *  and the bet is placed but then tabs away and AP is paused and not running but the AP counter still needs to decrease because the bet has been submitted.
     */
    if (autoplay.savedBet) {
      if (totalBet) {
        this.store.dispatch(autoplayActions.decRoundCount());
      }

      const bet = bets.betStack[bets.betStack.length - 1];
      if (bet) {
        const betsModified = this.areBetsModified() || !areBetsEqual(bet, autoplay.savedBet);
        if (betsModified && totalBet && autoplay.roundsLeft > 1) {
          this.store.dispatch(popupActions.pushPopup(PopupType.AutoplayModified));
        }
      }
    }

    return getComputedBets(bets.betStack);
  }

  /**
   * This method provides option on the game level extend condition for autoplay modify popup
   * @protected
   */
  protected areBetsModified(): boolean {
    return false;
  }

  private updateAcceptedBets(computedBets: ComputedBet[]): void {
    this.store.dispatch(betActions.setAcceptedBets(getAcceptedBets(computedBets)));
  }

  private placeBets(): void {
    const computedBets = this.computeBets();
    const totalBet = roundNumber(
      computedBets.reduce((sum, bet) => sum + bet.value, 0),
      this.store.getState().gameState.currencyInfo?.digitsNumber ?? 0
    );

    this.checkInactivity(totalBet === 0);
    this.updateAcceptedBets(computedBets);
  }

  private checkInactivity(inactiveRound = true): void {
    const state = this.store.getState();
    const inactiveRounds = state.gameState.inactivityCounter;
    if (inactiveRound) {
      const inactivityLimit = state.config.tableConfig?.idleLimit;
      if (inactivityLimit && inactiveRounds >= inactivityLimit) {
        this.store.dispatch(popupActions.pushPopup(PopupType.Inactivity));
        // Pause Game stream and game socket if inactivity limit is reached
        this.store.dispatch(gameStateActions.setGamePaused(true));
        this.store.dispatch(gameStateActions.resetCurrentGameStatus());
      } else {
        this.store.dispatch(gameStateActions.setInactivityCounter(inactiveRounds + 1));
      }
    } else if (inactiveRounds > 0) {
      this.store.dispatch(gameStateActions.setInactivityCounter(0));
    }
  }
}
